import { type CSSProperties, useCallback, useRef, useState } from "react";
import type { VirtualizerHandle } from "virtua";
import {
  fadeStyle,
  scrollSurfaceStyle,
  useAfterPaint,
  useFloatingScrollbar,
  useResizeObserver,
  useSubtreeMeasure,
} from "@tellescope/react-hooks";
import type { PaginatedListScrollbar, PaginatedListScrollOptions } from "./types";

interface UsePaginatedListOptions {
  onBottomReached?: () => void;
  onTopReached?: () => void;
  threshold: number;
  isLoading: boolean;
  scrollOptions?: PaginatedListScrollOptions;
}

/** Whether an element scrolls and its end is still below the fold. */
const isScrolledShort = (element: HTMLElement): boolean => {
  const { scrollTop, scrollHeight, clientHeight } = element;
  return scrollHeight > clientHeight + 1 && scrollTop + clientHeight < scrollHeight - 1;
};

export const usePaginatedList = ({
  onBottomReached,
  onTopReached,
  threshold,
  isLoading,
  scrollOptions,
}: UsePaginatedListOptions) => {
  const scrollElement = scrollOptions?.element;
  const scrollbar: PaginatedListScrollbar = scrollOptions?.type ?? "native";
  const fadeWhenClipped = scrollOptions?.fadeWhenClipped ?? false;
  const measureStartMargin = scrollOptions?.measureStartMargin ?? false;

  const virtualizerRef = useRef<VirtualizerHandle>(null);
  const topReachedRef = useRef(false);
  const bottomReachedRef = useRef(false);
  const scrollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isScrolling, setIsScrolling] = useState(false);
  const { scrollContainerRef, thumbRef, handleScroll: updateThumb } = useFloatingScrollbar();

  // `element` absent from `scrollOptions` entirely and `element: null` mean different things: the
  // former is "this list scrolls itself", the latter is "an ancestor scrolls, but its node has not
  // attached yet" — which must not fall back to self-scrolling, or the list would scroll on its own
  // for one render and then stop.
  const ownsScrolling = scrollOptions === undefined || !("element" in scrollOptions);
  const showFloatingThumb = ownsScrolling && scrollbar === "floating";

  // `virtua` takes a RefObject rather than an element, so the external scroller is mirrored into one.
  // Assigned during render so the virtualizer sees the current node on the same pass it is given, and
  // kept as a stable object so passing it never re-subscribes anything.
  const externalScrollRef = useRef<HTMLElement | null>(null);
  externalScrollRef.current = scrollElement ?? null;

  const { ref: clipRef, value: measuredClipped } = useResizeObserver<boolean>(isScrolledShort, false);
  const [scrolledClipped, setScrolledClipped] = useState<boolean>();
  // The fade needs this list to own its scroller — with an external `element` the offsets belong to a
  // container this component does not render. Gating the tracking as well as the read keeps
  // `handleScroll` from writing state nothing can observe, which would re-render the rows mid-scroll.
  const tracksClipping = ownsScrolling && fadeWhenClipped;
  const isClipped = tracksClipping && (scrolledClipped ?? measuredClipped);

  const startMarginRef = useRef<HTMLDivElement | null>(null);
  const shouldMeasureStartMargin = measureStartMargin && !ownsScrolling;

  // Memoized because `clipRef` tracks element *identity*: an inline callback makes React detach
  // (`null`) and re-attach the node every render, re-running the resize observer's effect each pass.
  const attachScroller = useCallback(
    (element: HTMLDivElement | null) => {
      startMarginRef.current = element;
      if (!ownsScrolling) return;
      scrollContainerRef.current = element;
      clipRef.current = element;
    },
    [ownsScrolling, scrollContainerRef, clipRef]
  );

  const { value: startMargin } = useSubtreeMeasure(
    scrollElement,
    (container) => {
      const element = startMarginRef.current;
      if (!element) return 0;
      const offset = element.getBoundingClientRect().top - container.getBoundingClientRect().top;
      return Math.max(0, Math.round(offset + container.scrollTop));
    },
    0,
    { enabled: shouldMeasureStartMargin }
  );

  const handleTopReached = useCallback(() => {
    if (!topReachedRef.current && onTopReached) {
      topReachedRef.current = true;
      bottomReachedRef.current = false;
      onTopReached();
    }
  }, [onTopReached]);

  const handleBottomReached = useCallback(() => {
    if (!bottomReachedRef.current && onBottomReached) {
      bottomReachedRef.current = true;
      topReachedRef.current = false;
      onBottomReached();
    }
  }, [onBottomReached]);

  const handleScroll = useCallback(
    (offset: number) => {
      const virtualizer = virtualizerRef.current;
      if (!virtualizer) return;

      const { scrollSize, viewportSize } = virtualizer;

      if (offset <= threshold) {
        handleTopReached();
      } else {
        topReachedRef.current = false;
      }

      if (offset - scrollSize + viewportSize >= -threshold) {
        handleBottomReached();
      } else {
        bottomReachedRef.current = false;
      }

      // No scroll-end event exists with usable support, so "the user stopped scrolling" is an idle
      // timer: the thumb shows on every scroll tick and hides once one elapses without another.
      if (scrollbar === "hidden-until-scroll") {
        setIsScrolling(true);
        if (scrollingTimeoutRef.current) clearTimeout(scrollingTimeoutRef.current);
        scrollingTimeoutRef.current = setTimeout(() => setIsScrolling(false), 1000);
      }

      if (scrollbar === "floating") updateThumb();
      if (tracksClipping) {
        setScrolledClipped(scrollSize > viewportSize && offset - scrollSize + viewportSize < -1);
      }
    },
    [threshold, handleTopReached, handleBottomReached, scrollbar, updateThumb, tracksClipping]
  );

  // Content short enough to fit its viewport never scrolls, so `virtua` — which reports only on real
  // scroll events — never fires, and the end of the list is never reached. A first page that
  // underfills its container would otherwise strand every page after it. `handleBottomReached`'s
  // latch keeps this to one call per settled content.
  //
  // Two frames because the virtualizer publishes its own measurement a frame after paint: read any
  // earlier and `viewportSize` is still 0, which the guard below (correctly) declines to act on.
  useAfterPaint(
    () => {
      const virtualizer = virtualizerRef.current;
      if (!virtualizer) return;
      const { scrollSize, viewportSize } = virtualizer;
      if (viewportSize > 0 && scrollSize <= viewportSize) handleBottomReached();
    },
    [handleBottomReached],
    { enabled: !isLoading, frames: 2 }
  );

  const surfaceStyle: CSSProperties | undefined = ownsScrolling
    ? scrollbar === "floating"
      ? scrollSurfaceStyle({ faded: isClipped })
      : fadeStyle({ faded: isClipped })
    : undefined;

  return {
    virtualizerRef,
    attachScroller,
    thumbRef,
    handleScroll,
    externalScrollRef,
    startMargin: shouldMeasureStartMargin ? startMargin : undefined,
    ownsScrolling,
    showFloatingThumb,
    scrollbar,
    isScrolling,
    surfaceStyle,
  };
};
